import styled, { css } from 'styled-components';
import { margin, padding, darken, transparentize } from 'polished';
import { animated } from 'react-spring';
import { Link } from 'react-router-dom';

import { FiberNew } from 'styled-icons/material';
import { X } from 'styled-icons/boxicons-regular';

interface NavbarStyleProps {
    $isOpen: boolean;
}
export const NavbarStyle = styled(animated.div)<NavbarStyleProps>`
    z-index: 3;
    background: ${(props) => props.theme.colors.black};
    position: relative;
    display: flex;
    flex-flow: column;
    flex-shrink: 0;
    width: ${(props) => (props.$isOpen ? '250px' : '75px')};
    height: 100%;
    margin: 0;
    overflow: hidden;
    transition: width 0.3s ease-in-out;

    @media only screen and (max-width: 600px) {
        display: none;
    }
`;

export const TotemIconContainer = styled.div`
    position: relative;
    display: flex;
    flex-flow: row;
    align-items: center;
    width: 100%;
    height: 75px;
    overflow: visible;

    &:hover .totem-popup {
        opacity: 1;
        visibility: visible;
    }
`;

export const TotemContent = styled.div`
    display: flex;
    flex-flow: row;
    align-items: center;
    width: 100%;
    height: 100%;
`;
interface TotemNameProps {
    $active: boolean;
}
export const TotemName = styled.span<TotemNameProps>`
    color: ${(props) => (props.$active ? props.theme.colors.greenDarker : props.theme.colors.white)};
    font-size: 12px;
    font-weight: bold;
    margin-left: 10px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
`;

interface TotemsIconsContainerProps {
    $isOpen: boolean;
}
export const TotemsIconsContainer = styled.div<TotemsIconsContainerProps>`
    overflow: auto;
    &:first-child {
        margin-top: 2em;
    }

    &::-webkit-scrollbar {
        display: none;
    }
`;

interface TotemIconItemProps {
    $imgsrc: string;
    $transparent?: boolean;
    $fullSize?: boolean;
    $imageSize: string;
    color: string;
    $active: boolean;
    isDisabled?: boolean;
}

export const TotemIconItem = styled(Link)<TotemIconItemProps>`
    width: 75px;
    height: 75px;
    background: url('${(props) => props.$imgsrc}') ${(props) => (props.$transparent ? 'transparent' : props.color)}
        no-repeat center / ${(props) => (props.$fullSize ? 'cover' : props.$imageSize || '95% 95%')};
    object-fit: cover;
    overflow: hidden;
    transition: all 0.25s ease;
    opacity: ${(props) => (props.$active ? 1 : 0.6)};
    cursor: pointer;
    flex-shrink: 0;

    &:last-of-type {
        margin: 0;
    }

    ${({ isDisabled }) =>
        isDisabled &&
        css`
            cursor: default;
            box-shadow: 0;
            pointer-events: none;
        `}
`;

interface ActiveMarkProps {
    $active: boolean;
}
export const ActiveMark = styled.span<ActiveMarkProps>`
    position: absolute;
    top: 0px;
    left: 0;
    width: 5px;
    height: 75px;
    background-color: ${(props) => props.theme.colors.greenDarker};
    opacity: ${(props) => (props.$active ? 1 : 0)};
    transition: opacity 0.4s ease;
`;

interface CloseIconProps {
    $active: boolean;
}
export const CloseIcon = styled(X)<CloseIconProps>`
    position: absolute;
    opacity: ${(props) => (props.$active ? 1 : 0.8)};
    right: 6px;
    top: 3px;
    color: white;
    background: ${(props) => darken(0.05, props.theme.colors.red)};
    border-radius: 100%;
    transition: all 0.25s ease;

    &:hover {
        background: ${(props) => props.theme.colors.red};
        cursor: pointer;
    }
`;

interface NewTotemIconProps {
    $active: boolean;
}
export const NewTotemIcon = styled(FiberNew)<NewTotemIconProps>`
    position: absolute;
    opacity: ${(props) => (props.$active ? 1 : 0.8)};
    right: 6px;
    top: 18px;
    color: ${(props) => props.theme.colors.white};
`;

interface TotemNamePopupProps {
    $top?: number;
    $left?: number;
    $show: boolean;
}
export const TotemNamePopup = styled.div<TotemNamePopupProps>`
    position: fixed;
    top: ${(props) => (props.$top ? `${props.$top}px` : '50%')};
    left: ${(props) => (props.$left ? `${props.$left}px` : `${props.theme.variables.globalNavbarWidth + 10}px`)};
    transform: translateY(-50%);
    padding: 6px 10px;
    background: rgba(0, 0, 0, 0.9);
    color: white;
    font-size: 12px;
    font-weight: 500;
    border-radius: 4px;
    white-space: nowrap;
    z-index: 1000;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    pointer-events: none;
    opacity: ${(props) => (props.$show ? 1 : 0)};
    visibility: ${(props) => (props.$show ? 'visible' : 'hidden')};
    transition: all 0.2s ease;
`;

interface DropdownContainerProps {
    $fontSize: string;
    $lowercase: boolean;
    $paddingMultiplier: number[];
}
export const DropdownContainer = styled.div<DropdownContainerProps>`
    position: fixed;
    left: ${(props) => props.theme.variables.globalNavbarWidth + props.theme.variables.padding}px;
    bottom: 17px; // used for pixel-perfect aligning the dropdown with the button calling it
    width: auto;
    min-width: ${(props) => props.theme.variables.specificNavbarWidth + 6 * props.theme.variables.padding}px;
    height: max-content;
    overflow-x: auto;
    overflow-y: auto;
    font-size: ${(props) => props.$fontSize};
    color: ${(props) => props.theme.colors.black};
    background: ${(props) => props.theme.colors.white};
    border-radius: ${(props) => props.theme.variables.borderRadius};
    box-shadow: 0 1px 7px 1px ${(props) => transparentize(0.85, props.theme.colors.black)};
    z-index: 10;

    > ul {
        display: flex;
        flex-flow: column;
        justify-content: center;
        margin: 0;
        padding: 0;

        a {
            text-decoration: none;

            &:link,
            &:visited,
            &:active,
            &:focus {
                color: ${(props) => props.theme.colors.black};
            }
        }

        li {
            transition: all 0.25s ease;
            display: flex;
            font-size: 1.25em;
            text-transform: ${(props) => (props.$lowercase ? 'none' : 'uppercase')};
            list-style: none;
            ${({
                theme: {
                    variables: { padding: pad },
                },
                $paddingMultiplier = [],
            }) =>
                padding(
                    pad * ($paddingMultiplier[0] || 0),
                    pad * ($paddingMultiplier[1] || 0),
                    pad * ($paddingMultiplier[2] || 0),
                    pad * ($paddingMultiplier[3] || 0),
                )};

            &:hover {
                color: ${(props) => props.theme.colors.black};
                cursor: pointer;
                background-color: ${(props) => props.theme.colors.green};
            }

            > img {
                width: 16px;
                ${(props) => margin(0, props.theme.variables.margin, 0, 0)};
            }

            > svg {
                ${(props) => margin(0, props.theme.variables.margin * 0.25, 0, 0)};
            }
        }
    }
`;
interface NavBarHeaderProps {
    $isOpen: boolean;
}
export const NavBarHeader = styled.div<NavBarHeaderProps>`
    display: flex;
    align-items: center;
    justify-content: left;
    padding: 20px 10px 0;
    margin-left: 15px;
    color: ${(props) => props.theme.colors.white};
    cursor: pointer;
    transition: all 0.3s ease;
    min-width: 75px;
`;
